import './styles.scoped.css'
import {useEffect, useMemo, useRef, useState} from 'react'
import {Typography, Button, IconButton, Box, Stepper, Step, StepLabel, Tabs, Tab} from '@mui/material'
import {ArrowBack, CheckCircle} from '@mui/icons-material'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {useNavigate, Link as LinkRoute} from 'react-router-dom'
import {phoneRegex, parseError} from '../../utils/helpers'
import {loadServiceCatalog} from '../../utils/serviceCatalog'
import {useMobile} from '../../hooks'
import {adminApi, evvApi} from '../../services'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {useAuth} from '../../contexts/AuthContext'
import {ENVIRONMENTS} from '../AdminOrganizationDetails/components'
import {GeneralInformation, Branches, EvvCredentials, Users, Review} from './components'
import {
    DEFAULT_ORGANIZATION_TYPE,
    tooLong,
    optionalEmail,
    optionalPhone,
    requiredEmail,
    digitsOf,
    emptyBranch,
    emptyUser,
    hasContent,
    payerKey,
    branchNeedsEvv,
    branchCodes,
    toBranchPayload,
} from './helpers'

const GENERAL = 0
const BRANCHES = 1
const EVV = 2
const USERS = 3
const REVIEW = 4

const address = yup.object().shape({
    street1: yup.string().trim().required('Street is required').max(255, tooLong(255)),
    street2: yup.string().max(255, tooLong(255)),
    city: yup.string().trim().required('City is required').max(100, tooLong(100)),
    state: yup.string().required('State is required').max(50, tooLong(50)),
    zip: yup.string().trim().required('ZIP code is required').max(20, tooLong(20)),
})

export default () => {
    const {mobile} = useMobile()
    // Below this the step list beside the form would squeeze the form, so it
    // goes across the top instead.
    const {mobile: narrow} = useMobile(1199)
    const navigate = useNavigate()
    const {setLoading, setMessageBox} = useGlobalStates()
    const {organization: ownOrganization} = useAuth()
    const [activeStep, setActiveStep] = useState(GENERAL)
    const [logoUrl, setLogoUrl] = useState(null)
    const [logoFile, setLogoFile] = useState(null)
    const [roles, setRoles] = useState([])
    const [serviceCatalog, setServiceCatalog] = useState([])
    const [payerCodes, setPayerCodes] = useState([])
    const [generalData, setGeneralData] = useState(null)
    const [branchesData, setBranchesData] = useState(null)
    const [evvData, setEvvData] = useState(null)
    const [usersData, setUsersData] = useState(null)

    // Which services need EVV decides which branches enrol and whether the
    // EVV step is needed at all. The branch schema reads it through a ref, as
    // the catalog arrives after the form is made.
    const evvServiceIds = useMemo(() => new Set(serviceCatalog.filter(service => service.requires_evv).map(service => service.id)), [serviceCatalog])
    const evvServiceIdsRef = useRef(evvServiceIds)
    evvServiceIdsRef.current = evvServiceIds

    const generalForm = useForm({
        defaultValues: {name: '', email: '', phone: '', npi: '', tin: '', federal_tax_id: ''},
        resolver: yupResolver(
            yup.object().shape({
                name: yup.string().trim().required('Healthcare name is required').min(2, 'Must be at least 2 characters').max(255, tooLong(255)),
                email: requiredEmail(255),
                phone: yup
                    .string()
                    .trim()
                    .required('Phone number is required')
                    .max(20, tooLong(20))
                    .test('is-valid-phone', 'Invalid phone number', value => phoneRegex.test(value)),
                // Both optional, and checked as the backend checks them. A TIN
                // may be typed with its dash, which is dropped when it is sent.
                npi: yup
                    .string()
                    .trim()
                    .test('npi', 'Organization NPI must be 10 digits', value => !value || /^\d{10}$/.test(value)),
                tin: yup
                    .string()
                    .trim()
                    .test('tin', 'US Taxpayer ID must be 9 digits', value => !value || (/^[\d\s-]+$/.test(value) && digitsOf(value).length === 9)),
                // HHAeXchange's provider tax ID: nine digits. Dashes are allowed
                // here and dropped before it is sent.
                federal_tax_id: yup
                    .string()
                    .trim()
                    .test('ein', 'Federal Tax ID must be 9 digits', value => !value || (/^[\d\s-]+$/.test(value) && digitsOf(value).length === 9)),
            }),
        ),
    })

    const needsEvvField = message =>
        function (value) {
            const branch = this.from?.[1]?.value
            return !branchNeedsEvv(branch, evvServiceIdsRef.current) || !!value?.trim() || this.createError({message})
        }

    const branchesForm = useForm({
        defaultValues: {branches: [emptyBranch(true)]},
        resolver: yupResolver(
            yup.object().shape({
                branches: yup
                    .array()
                    .of(
                        yup.object().shape({
                            branch_name: yup.string().trim().required('Branch name is required').max(255, tooLong(255)),
                            // A branch has to be reachable: the backend refuses one with neither.
                            email: optionalEmail(120).test('email-or-phone', 'Enter a branch email or phone number', function (value) {
                                return !!value?.trim() || !!this.parent.phone_number?.trim()
                            }),
                            phone_number: optionalPhone(120),
                            provider_number: yup.string().trim().required('Branch NPI is required').max(125, tooLong(125)),
                            umpi: yup
                                .string()
                                .trim()
                                .test(
                                    'umpi',
                                    'Branch UMPI must be letters and digits, at most 20',
                                    value => !value || /^[A-Za-z0-9]{1,20}$/.test(value),
                                ),
                            address,
                            service_ids: yup.array().min(1, 'Add at least one service'),
                            payerDraftOpen: yup.boolean().test('draft-saved', 'Save or cancel this payor before continuing', function (open) {
                                return !open || !hasContent(this.parent.payerDraft)
                            }),
                            evv: yup.object().shape({
                                office_qualifier: yup.string().test('needed', needsEvvField('Office qualifier is required')),
                                office_identifier: yup.string().max(64, tooLong(64)).test('needed', needsEvvField('Office identifier is required')),
                            }),
                        }),
                    )
                    // The backend keeps a branch NPI unique within the organization.
                    .test('unique-npi', function (branches) {
                        const seen = new Set()
                        for (const [index, branch] of (branches || []).entries()) {
                            const npi = branch?.provider_number?.trim()
                            if (!npi) continue
                            if (seen.has(npi))
                                return this.createError({path: `branches[${index}].provider_number`, message: 'Another branch already has this NPI'})
                            seen.add(npi)
                        }
                        return true
                    }),
            }),
        ),
    })

    const evvForm = useForm({
        defaultValues: {client_id: '', client_secret: '', base_url: ENVIRONMENTS[0].value, api_version: 'v2'},
        resolver: yupResolver(
            yup.object().shape({
                // Optional, but only as a pair: one without the other cannot sign in.
                client_id: yup
                    .string()
                    .max(255, tooLong(255))
                    .test('with-secret', 'Client ID is required with a client secret', function (value) {
                        return !!value?.trim() || !this.parent.client_secret?.trim()
                    }),
                client_secret: yup.string().test('with-id', 'Client secret is required with a client ID', function (value) {
                    return !!value?.trim() || !this.parent.client_id?.trim()
                }),
                base_url: yup.string(),
                api_version: yup.string(),
            }),
        ),
    })

    const usersForm = useForm({
        defaultValues: {users: [], draft: emptyUser(), draftOpen: true},
        resolver: yupResolver(
            yup.object().shape({
                users: yup.array().min(1, 'Add at least one user, so someone can sign in to this organization'),
                draftOpen: yup.boolean().test('draft-saved', 'Add or cancel this user before continuing', function (open) {
                    const {first_name, last_name, email, phone} = this.parent.draft || {}
                    return !open || !hasContent({first_name, last_name, email, phone})
                }),
            }),
        ),
    })

    // Every page of the catalog. A failed load is said, since an empty picker
    // otherwise looks like there are no services at all.
    const refreshServiceCatalog = () =>
        loadServiceCatalog()
            .then(setServiceCatalog)
            .catch(error => {
                const {title, description} = parseError(error)
                setMessageBox({
                    status: 'error',
                    title: title || 'Could not load the services',
                    description: description || 'The service list could not be loaded. Please try again later.',
                })
            })

    // Asked again on reaching Branches if it came back empty, e.g. the first
    // load failed.
    useEffect(() => {
        if (activeStep === BRANCHES && !serviceCatalog.length) refreshServiceCatalog()
    }, [activeStep])

    useEffect(() => {
        refreshServiceCatalog()
        evvApi
            .getPayerCodes()
            .then(({data}) => setPayerCodes(data?.codes || []))
            .catch(() => {})
        // The organization does not exist yet, so its roles are asked of our
        // own: the system roles are shared by every organization, custom ones
        // are not and are left out.
        if (ownOrganization?.id) {
            adminApi
                .listOrgRoles(ownOrganization.id)
                .then(({data: {data}}) => setRoles((data?.roles || []).filter(role => role.is_system)))
                .catch(() => {})
        }
    }, [])

    // The first user runs the organization, so they start as its administrator.
    useEffect(() => {
        const admin = roles.find(role => role.name === 'admin') || roles[0]
        if (admin && !usersForm.getValues('draft.role_id')) usersForm.setValue('draft.role_id', admin.id)
    }, [roles])

    const liveBranches = branchesForm.watch('branches')
    const evvNeeded = (branchesData?.branches || liveBranches || []).some(branch => branchNeedsEvv(branch, evvServiceIds))

    // Users only keep branches that still exist.
    const prepareUsers = branches => {
        const keys = branches.map(branch => branch.key)
        const keep = branchKeys => {
            const kept = (branchKeys || []).filter(key => !key || keys.includes(key))
            return kept.length ? kept : ['main']
        }
        const {users, draft} = usersForm.getValues()
        usersForm.setValue(
            'users',
            users.map(user => ({...user, branch_keys: keep(user.branch_keys)})),
        )
        usersForm.setValue('draft.branch_keys', keep(draft.branch_keys))
    }

    const next = () => {
        if (activeStep === GENERAL) {
            generalForm.handleSubmit(data => {
                setGeneralData(data)
                setActiveStep(BRANCHES)
            })()
        } else if (activeStep === BRANCHES) {
            branchesForm.handleSubmit(data => {
                setBranchesData(data)
                prepareUsers(data.branches)
                setActiveStep(data.branches.some(branch => branchNeedsEvv(branch, evvServiceIds)) ? EVV : USERS)
            })()
        } else if (activeStep === EVV) {
            evvForm.handleSubmit(data => {
                setEvvData(data)
                setActiveStep(USERS)
            })()
        } else if (activeStep === USERS) {
            usersForm.handleSubmit(data => {
                setUsersData(data)
                setActiveStep(REVIEW)
            })()
        } else {
            submit()
        }
    }

    const back = () => setActiveStep(step => (step === USERS && !evvNeeded ? BRANCHES : step - 1))

    const cancel = () => navigate('/admin/organizations')

    // Everything is sent here, from the review. Only the organization itself is
    // all-or-nothing: once it exists, a part that fails is reported and the
    // rest carries on, since each can be set up again from its tabs.
    const submit = async () => {
        const branches = branchesData.branches
        const evv = evvNeeded ? evvData || {} : {}
        setLoading(true)
        let org
        try {
            const {data} = await adminApi.createOrganization({
                name: generalData.name.trim(),
                type: DEFAULT_ORGANIZATION_TYPE,
                email: generalData.email.trim(),
                phone: generalData.phone.trim(),
                // Sent only when given; the backend stores a missing one as none.
                ...(generalData.npi?.trim() && {npi: generalData.npi.trim()}),
                ...(generalData.tin?.trim() && {tin: digitsOf(generalData.tin)}),
                // No address is asked for, but one has to be sent: the backend
                // writes a missing address as invalid JSON and refuses the create.
                address: {},
            })
            org = data?.data
        } catch (error) {
            setLoading(false)
            const {title, description} = parseError(error)
            setMessageBox({
                status: 'error',
                title: title || 'Organization Add Failed',
                description: description || 'There was an error adding this organization. Please try again later.',
            })
            return
        }

        const problems = []
        const attempt = async (what, request) => {
            try {
                return await request()
            } catch (error) {
                const {title, description} = parseError(error)
                problems.push(`${what}: ${description || title || 'not saved'}`)
                return null
            }
        }

        if (logoFile) {
            const formData = new FormData()
            formData.append('file', logoFile)
            await attempt('Logo', () => adminApi.uploadOrganizationLogo(org.id, formData))
        }

        // Creating an organization does not take its Federal Tax ID; it is an
        // EVV setting, written once the organization exists.
        if (generalData.federal_tax_id?.trim()) {
            await attempt('Federal Tax ID', () => adminApi.updateOrganization(org.id, {federal_tax_id: digitsOf(generalData.federal_tax_id)}))
        }

        // The main branch is the default one created with the organization.
        let defaultBranchId = org?.branch?.id
        if (!defaultBranchId) {
            const response = await attempt('Main branch', () => adminApi.listBranches(org.id))
            defaultBranchId = response?.data?.data?.branches?.find(branch => branch.branch_code === org.id)?.id
        }

        const codes = branchCodes(branches)
        const branchIds = {}
        for (const branch of branches) {
            if (branch.main) {
                if (defaultBranchId) {
                    branchIds[branch.key] = defaultBranchId
                    await attempt(branch.branch_name, () => adminApi.updateBranch(org.id, defaultBranchId, toBranchPayload(branch, org.id)))
                    continue
                }
                // The backend only logs a failure to make the default branch, so
                // it is made here instead, with the code that marks it default.
                const response = await attempt(branch.branch_name, () => adminApi.createBranch(org.id, toBranchPayload(branch, org.id)))
                defaultBranchId = response?.data?.data?.id
                if (defaultBranchId) branchIds[branch.key] = defaultBranchId
            } else {
                const response = await attempt(branch.branch_name, () => adminApi.createBranch(org.id, toBranchPayload(branch, codes[branch.key])))
                if (response?.data?.data?.id) branchIds[branch.key] = response.data.data.id
            }
        }

        // Payors belong to the organization, one per name and service category,
        // and each new one is given to every branch. So each is created once,
        // then every branch is narrowed to the payors entered on it.
        const payerIds = {}
        for (const payer of branches.flatMap(branch => branch.payers)) {
            const key = payerKey(payer)
            if (key in payerIds) continue
            payerIds[key] = null
            const response = await attempt(payer.payer_name, () =>
                adminApi.createPayer(org.id, {
                    payer_name: payer.payer_name,
                    payer_type: payer.payer_type,
                    service_category: payer.service_category,
                    provider_number: payer.provider_number,
                }),
            )
            const payerId = response?.data?.data?.id
            if (!payerId) continue
            payerIds[key] = payerId
            // Creating a payor does not take its EVV code; it is set once it exists.
            await attempt(`${payer.payer_name} EVV code`, () => adminApi.updatePayer(org.id, payerId, {hhax: {payer_code: payer.payer_code}}))
        }
        // Every branch is sent its list, an empty one included: a branch left out
        // would keep every payor it was just given. The backend reads an empty
        // list as "none" and a missing one as "leave alone".
        if (Object.values(payerIds).some(Boolean)) {
            for (const branch of branches) {
                if (!branchIds[branch.key]) continue
                const ids = branch.payers.map(payer => payerIds[payerKey(payer)]).filter(Boolean)
                await attempt(`${branch.branch_name} payors`, () =>
                    adminApi.updateBranch(org.id, branchIds[branch.key], {payers: ids.map(payer_id => ({payer_id}))}),
                )
            }
        }

        if (evv.client_id?.trim()) {
            const response = await attempt('EVV credentials', () =>
                adminApi.setHhaxCredentials(org.id, {
                    client_id: evv.client_id.trim(),
                    client_secret: evv.client_secret.trim(),
                    base_url: evv.base_url,
                    api_version: evv.api_version,
                }),
            )
            // Stored, but not checked: HHAeXchange could not be reached.
            if (response?.data?.data?.warning) problems.push(`EVV credentials: ${response.data.data.warning}`)
        }

        for (const branch of branches) {
            if (!branchNeedsEvv(branch, evvServiceIds) || !branchIds[branch.key]) continue
            await attempt(`EVV enrollment for ${branch.branch_name}`, () =>
                adminApi.updateBranch(org.id, branchIds[branch.key], {
                    hhax: {enabled: true, office_qualifier: branch.evv.office_qualifier, office_identifier: branch.evv.office_identifier.trim()},
                }),
            )
        }

        for (const user of usersData.users) {
            const ids = user.branch_keys.map(key => branchIds[key]).filter(Boolean)
            const email = user.email.trim().toLowerCase()
            const response = await attempt(email, () =>
                adminApi.createOrgUser(org.id, {
                    email,
                    first_name: user.first_name.trim(),
                    last_name: user.last_name.trim(),
                    phone: user.phone?.trim() || '',
                    role_id: user.role_id,
                    branch_ids: ids,
                    primary_branch_id: ids[0],
                    send_invite: true,
                }),
            )
            // The account can exist while its invitation email did not go out.
            if (response?.data?.data?.warning) problems.push(`${email}: ${response.data.data.warning}`)
        }

        setLoading(false)
        navigate(`/admin/organizations/${org.id}`, {
            state: {
                toast: problems.length
                    ? {status: 'warning', title: 'Organization added, but not everything was saved', description: problems.join(' · ')}
                    : {status: 'success', title: 'Organization Added Successfully', description: 'This organization has been added successfully.'},
            },
        })
    }

    const steps = [
        {label: 'General Information'},
        {label: 'Branches'},
        {label: 'EVV credentials', skipped: !evvNeeded, caption: !evvNeeded && 'no EVV service selected'},
        {label: 'Users'},
        {label: 'Review & Create'},
    ]

    const primaryLabel = activeStep === REVIEW ? 'Create healthcare organization' : 'Next'
    const secondary =
        activeStep === GENERAL ? (
            <Button variant="outlined" onClick={cancel} style={mobile ? {flex: 1} : undefined}>
                Cancel
            </Button>
        ) : (
            <Button variant="outlined" onClick={back} style={mobile ? {flex: 1} : undefined}>
                Back
            </Button>
        )

    const content = (
        <>
            {activeStep === GENERAL && <GeneralInformation form={generalForm} logoUrl={logoUrl} setLogoUrl={setLogoUrl} setLogoFile={setLogoFile} />}
            {activeStep === BRANCHES && (
                <Branches form={branchesForm} serviceCatalog={serviceCatalog} payerCodes={payerCodes} evvServiceIds={evvServiceIds} />
            )}
            {activeStep === EVV && <EvvCredentials form={evvForm} />}
            {activeStep === USERS && <Users form={usersForm} roles={roles} branches={branchesData?.branches || []} />}
            {activeStep === REVIEW && (
                <Review
                    general={generalData}
                    logoUrl={logoUrl}
                    branches={branchesData.branches}
                    evv={evvData || {}}
                    evvNeeded={evvNeeded}
                    users={usersData.users}
                    roles={roles}
                    serviceCatalog={serviceCatalog}
                    evvServiceIds={evvServiceIds}
                    goTo={setActiveStep}
                />
            )}
        </>
    )

    return (
        <>
            {mobile ? (
                <>
                    <div className="header">
                        <IconButton onClick={activeStep === GENERAL ? cancel : back}>
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="h6" color="text.primary">
                            Add Organization
                        </Typography>
                    </div>
                    <div className="actions">
                        {secondary}
                        <Button variant="contained" onClick={next} style={{flex: 1}}>
                            {primaryLabel}
                        </Button>
                    </div>
                    <br />
                    <Tabs value={activeStep} variant="scrollable">
                        {steps.map(step => (
                            <Tab key={step.label} label={step.label} disabled />
                        ))}
                    </Tabs>
                    <br />
                    {content}
                </>
            ) : (
                <>
                    <Typography variant="body1" color="text.secondary">
                        <LinkRoute className="link" to="/admin/organizations">
                            Organizations
                        </LinkRoute>
                        {' / Add organization'}
                    </Typography>
                    <br />
                    <div className="header">
                        <div className="back-container">
                            <div className="back-button" onClick={cancel}>
                                <ArrowBack style={{color: '#ffffff'}} fontSize="small" />
                            </div>
                            <Typography variant="h5" color="text.primary">
                                Add Organization
                            </Typography>
                        </div>
                        <div className="actions">
                            {secondary}
                            <Button variant="contained" onClick={next}>
                                {primaryLabel}
                            </Button>
                        </div>
                    </div>
                    <br />
                    <br />
                    <div className={narrow ? 'container stacked' : 'container'}>
                        <div className="step">
                            <Stepper activeStep={activeStep} orientation={narrow ? 'horizontal' : 'vertical'} alternativeLabel={narrow}>
                                {steps.map((step, index) => (
                                    <Step key={step.label} completed={!step.skipped && index < activeStep}>
                                        <StepLabel
                                            optional={
                                                step.caption && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {step.caption}
                                                    </Typography>
                                                )
                                            }
                                            slots={{
                                                stepIcon: ({active, completed, icon}) =>
                                                    completed ? (
                                                        <CheckCircle color="primary" style={{fontSize: 28}} />
                                                    ) : (
                                                        <Box
                                                            className="step-icon"
                                                            sx={theme => ({
                                                                backgroundColor: active ? theme.palette.primary.main : theme.palette.text.disabled,
                                                            })}
                                                        >
                                                            {step.skipped ? '-' : icon}
                                                        </Box>
                                                    ),
                                            }}
                                        >
                                            <Typography variant="subtitle2">{step.label}</Typography>
                                        </StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </div>
                        <div className="content">{content}</div>
                    </div>
                </>
            )}
            <br />
            <br />
            <br />
            <br />
        </>
    )
}
